import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { FormatBoldOutlined, FormatItalicOutlined, FormatUnderlinedOutlined } from '@mui/icons-material';
import {
  LexicalEditor,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
  TextFormatType,
} from 'lexical';

import { IS_APPLE } from '../../utils/environment';
import { translate as t } from 'hooks/useTranslations';

type TextFormatProps = {
  editor: LexicalEditor;
  disabled?: boolean;
};

export default function TextFormat({ editor, disabled = false }: TextFormatProps) {
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const $updateLinkToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection?.hasFormat('bold'));
      setIsItalic(selection?.hasFormat('italic'));
      setIsUnderline(selection?.hasFormat('underline'));
    } else {
      setIsBold(false);
      setIsItalic(false);
      setIsUnderline(false);
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateLinkToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, $updateLinkToolbar]);

  const handleFormat = useCallback(
    (format: TextFormatType) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.formatText(format);
        }
      });
    },
    [editor]
  );

  return (
    <Box>
      <IconButton
        size='small'
        disabled={disabled}
        onClick={() => handleFormat('bold')}
        className='toolbar-item'
        color={isBold ? 'primary' : 'neutral'}
        title={
          IS_APPLE
            ? `${t('contentEditor.toolbar.format.bold')} (⌘B)`
            : `${t('contentEditor.toolbar.format.bold')} (Ctrl+B)`
        }
        aria-label={t('contentEditor.toolbar.format.bold')}
      >
        <FormatBoldOutlined fontSize='small' />
      </IconButton>
      <IconButton
        size='small'
        disabled={disabled}
        onClick={() => handleFormat('italic')}
        className='toolbar-item'
        color={isItalic ? 'primary' : 'neutral'}
        title={
          IS_APPLE
            ? `${t('contentEditor.toolbar.format.italic')} (⌘I)`
            : `${t('contentEditor.toolbar.format.italic')} (Ctrl+I)`
        }
        aria-label={t('contentEditor.toolbar.format.italic')}
      >
        <FormatItalicOutlined fontSize='small' />
      </IconButton>
      <IconButton
        size='small'
        disabled={disabled}
        onClick={() => handleFormat('underline')}
        className='toolbar-item'
        color={isUnderline ? 'primary' : 'neutral'}
        title={
          IS_APPLE
            ? `${t('contentEditor.toolbar.format.underline')} (⌘U)`
            : `${t('contentEditor.toolbar.format.underline')} (Ctrl+U)`
        }
        aria-label={t('contentEditor.toolbar.format.underline')}
      >
        <FormatUnderlinedOutlined fontSize='small' />
      </IconButton>
    </Box>
  );
}
